import { useEffect, useMemo, useState } from "react";
import { HeroUIProvider, Input, Select, SelectItem, Chip, Button, Skeleton } from "@heroui/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { MagnifyingGlass, CheckCircle, ArrowCounterClockwise, Envelope, Phone, Globe, ChatCircleText } from "@phosphor-icons/react";

const TZ = "Asia/Dubai";
const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { timeZone: TZ, day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const KIND = { general: { label: "Enquiry", color: "primary" }, wholesale_quote: { label: "Price list", color: "warning" } };

export default function Enquiries() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("new");
  const [kind, setKind] = useState("all");
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/contact").then((r) => setRows(r.data || [])).catch(() => { setRows([]); toast.error("Could not load enquiries"); }); }, []);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => (rows || []).filter((r) =>
    (status === "all" || (r.status || "new") === status) && (kind === "all" || (r.kind || "general") === kind) &&
    (!q || [r.full_name, r.email, r.company, r.country, r.message].some((v) => (v || "").toLowerCase().includes(q)))), [rows, q, status, kind]);
  const newCount = (rows || []).filter((r) => (r.status || "new") === "new").length;

  const setRowStatus = async (r, next) => {
    setBusy(r.id);
    try {
      const { data } = await api.patch(`/contact/${r.id}`, { status: next });
      setRows((rs) => rs.map((x) => (x.id === r.id ? data : x)));
      toast.success(next === "handled" ? "Marked as handled" : "Reopened");
    } catch { toast.error("Update failed"); } finally { setBusy(""); }
  };

  return (
    <HeroUIProvider>
      <div className="space-y-5" data-testid="enquiries-page">
        <PageHeader title="Website Enquiries" subtitle={rows === null ? "Loading…" : `${newCount} new · ${rows.length} total`} />

        <div className="hp-card p-3 sm:p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="enquiries-filters">
          <Input aria-label="Search enquiries" placeholder="Search name, company, email, country…" value={search} onValueChange={setSearch} variant="bordered" radius="lg" size="lg"
            startContent={<MagnifyingGlass size={16} className="text-[#64748B]" />} isClearable onClear={() => setSearch("")} classNames={{ input: "text-base", inputWrapper: "min-h-11" }} data-testid="enquiries-search" />
          <div className="grid grid-cols-2 md:flex gap-2 md:shrink-0">
            <Select aria-label="Status" selectedKeys={[status]} onSelectionChange={(k) => setStatus([...k][0] || "all")} variant="bordered" radius="lg" size="sm" className="md:w-40" classNames={{ trigger: "min-h-11" }} data-testid="enquiries-status-filter">
              <SelectItem key="new">New</SelectItem>
              <SelectItem key="handled">Handled</SelectItem>
              <SelectItem key="all">All</SelectItem>
            </Select>
            <Select aria-label="Type" selectedKeys={[kind]} onSelectionChange={(k) => setKind([...k][0] || "all")} variant="bordered" radius="lg" size="sm" className="md:w-44" classNames={{ trigger: "min-h-11" }} data-testid="enquiries-kind-filter">
              <SelectItem key="all">All types</SelectItem>
              <SelectItem key="general">Enquiries</SelectItem>
              <SelectItem key="wholesale_quote">Price list requests</SelectItem>
            </Select>
          </div>
        </div>

        {rows === null ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No enquiries" hint={status === "new" ? "Everything from the website has been handled." : "Nothing matches these filters."} />
        ) : (
          <div className="space-y-3" data-testid="enquiries-list">
            {filtered.map((r) => <EnquiryCard key={r.id} r={r} busy={busy === r.id} onStatus={setRowStatus} />)}
          </div>
        )}
      </div>
    </HeroUIProvider>
  );
}

function EnquiryCard({ r, busy, onStatus }) {
  const k = KIND[r.kind] || KIND.general;
  const handled = r.status === "handled";
  return (
    <div className={`hp-card p-4 sm:p-5 ${handled ? "opacity-75" : ""}`} data-testid={`enquiry-${r.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Chip size="sm" color={k.color} variant="flat">{k.label}</Chip>
            {handled ? <Chip size="sm" color="success" variant="flat" startContent={<CheckCircle size={13} />}>Handled</Chip> : <Chip size="sm" color="danger" variant="dot">New</Chip>}
            <span className="text-xs text-[#64748B]">{fmt(r.created_at)}</span>
          </div>
          <div className="mt-2 font-semibold text-foreground">{r.full_name}{r.company ? <span className="font-normal text-[#64748B]"> · {r.company}</span> : null}{r.role ? <span className="font-normal text-[#64748B]"> · {r.role}</span> : null}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#64748B]">
            <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 hover:text-[#245FAF]"><Envelope size={14} />{r.email}</a>
            {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#245FAF]"><Phone size={14} />{r.phone}</a>}
            {r.country && <span className="inline-flex items-center gap-1.5"><Globe size={14} />{r.country}{r.region ? ` (${r.region})` : ""}</span>}
          </div>
          <div className="mt-3 flex gap-2 rounded-xl bg-muted/60 p-3 text-sm whitespace-pre-wrap"><ChatCircleText size={16} className="shrink-0 mt-0.5 text-[#377DD3]" /><span>{r.message}</span></div>
          {handled && <div className="mt-2 text-xs text-[#64748B]">Handled by {r.handled_by || "team"} · {fmt(r.handled_at)}</div>}
        </div>
        <div className="flex sm:flex-col gap-2 shrink-0">
          <Button as="a" href={`mailto:${r.email}?subject=${encodeURIComponent("Re: your enquiry to Heliopolis Pharmaceuticals")}`} size="sm" variant="bordered" radius="full" startContent={<Envelope size={14} />} data-testid={`enquiry-reply-${r.id}`}>Reply</Button>
          {handled
            ? <Button size="sm" variant="light" radius="full" isLoading={busy} onPress={() => onStatus(r, "new")} startContent={<ArrowCounterClockwise size={14} />} data-testid={`enquiry-reopen-${r.id}`}>Reopen</Button>
            : <Button size="sm" color="success" radius="full" className="text-white" isLoading={busy} onPress={() => onStatus(r, "handled")} startContent={<CheckCircle size={14} />} data-testid={`enquiry-handle-${r.id}`}>Mark handled</Button>}
        </div>
      </div>
    </div>
  );
}
